import { useCallback, useRef } from 'react'

import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import Product from '@src/quince-site-common/src/domains/Product'
import ProductMedia from '@src/quince-site-common/src/domains/ProductMedia'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { StyliticsJumplinkTrackingConfig } from '@src/quince-site-pdp/src/organisms/productPageSections/Stylitics/utils'
import { getColorwayId } from '@src/telemetry/integrations/provider-utils/stylitics-utils'
import { STYLITICS_CLIENT } from '@src/telemetry/integrations/Stylitics'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { StyliticsInsights } from '@src/telemetry/telemetry-pixels.types'
import { trackEvent } from '@src/telemetry/track'

const MAX_RETRIES = 2
const RETRY_DELAY = 200

export const trackJumplinkClickInternal = (
  product: Product | undefined,
  selectedProductMedia: ProductMedia | undefined,
) => {
  const productProperties = {
    product_id: product?.spid?.toString(),
    title: product?.title,
    color: selectedProductMedia?.options?.find(option => option.name === 'Color')?.values[0],
    tags: product?.tags,
    subClassId: product?.subClassId,
  }
  trackEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, {
    //eslint-disable-next-line i18next/no-literal-string
    entity_type: 'action',
    action: 'click_complete_the_look_stylitics',
    context: {
      ...StyliticsJumplinkTrackingConfig,
      productProperties,
    },
  })
}

export const scrollToStyliticsWidget = () => {
  const element = document.querySelector('#stylitics-widget-title')
  if (!element) return
  const rect = element.getBoundingClientRect()
  const scrollTop = window.scrollY || document.documentElement.scrollTop
  window.scrollTo({
    top: rect.top + scrollTop - 20,
    behavior: 'smooth',
  })
}

export const handleJumplinkClick = ({
  insights,
  product,
  selectedProductMedia,
}: {
  insights?: StyliticsInsights
  product: Product | undefined
  selectedProductMedia: ProductMedia | undefined
}) => {
  trackJumplinkClickInternal(product, selectedProductMedia)
  if (insights?.trackJumplinkClick) {
    insights.trackJumplinkClick()
  }
  scrollToStyliticsWidget()
}

export const initStyliticsInsights = async ({
  isStyliticsBucket,
  colorwayId,
  insightsRef,
  client,
  isMounted,
  setRef,
}: {
  isStyliticsBucket: boolean
  colorwayId?: string
  insightsRef: StyliticsInsights | undefined
  client: string
  isMounted: boolean
  setRef: (insights: StyliticsInsights, colorwayId?: string) => void
}) => {
  if (!isStyliticsBucket || !colorwayId || !isMounted) {
    return
  }
  let retryCount = 0
  while (retryCount < MAX_RETRIES && insightsRef?.create) {
    try {
      retryCount++
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * retryCount))
      const insights = (await insightsRef.create({
        client,
        itemId: colorwayId,
      })) as StyliticsInsights
      setRef(insights, colorwayId)
      return
    } catch (err) {
      captureException(err, {
        module: MODULES.PRODUCT,
        feature: FEATURES.STYLITICS_JUMPLINK,
      })
      retryCount++
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * retryCount))
    }
  }
}

export const useStyliticsJumpLink = (
  product: Product | undefined,
  selectedProductMedia: ProductMedia | undefined,
  isStyliticsBucket: boolean,
) => {
  const itemColor = selectedProductMedia?.options?.find(option => option.name === 'Color')?.values[0]
  const colorwayId = getColorwayId(product?.spid?.toString(), itemColor)
  const styliticsInsightsRef = useRef<{
    insights: StyliticsInsights
    colorwayId: string | undefined
  } | null>(null)

  const initStylitics = useCallback(
    (isMounted: boolean) => {
      initStyliticsInsights({
        isStyliticsBucket,
        colorwayId,
        insightsRef: window?.StyliticsInsights as StyliticsInsights,
        client: STYLITICS_CLIENT,
        isMounted,
        setRef: (insights, cw) => {
          styliticsInsightsRef.current = { insights, colorwayId: cw }
        },
      }).catch((err: unknown) => {
        captureException(err, {
          module: MODULES.PRODUCT,
          feature: FEATURES.STYLITICS_JUMPLINK,
        })
      })
    },
    [isStyliticsBucket, colorwayId],
  )

  const onJumplinkClick = useCallback(() => {
    try {
      const insights = styliticsInsightsRef.current?.insights
      handleJumplinkClick({ insights, product, selectedProductMedia })
    } catch (err) {
      captureException(err, {
        module: MODULES.PRODUCT,
        feature: FEATURES.STYLITICS_JUMPLINK,
      })
    }
  }, [product, selectedProductMedia])

  return { initStylitics, onJumplinkClick, colorwayId }
}
